import React, { useEffect, useMemo, useState } from "react";
import { getAuth, getCurrentUser } from "../../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const buildDocLabel = (doc) => {
  if (!doc) return "Document";
  if (doc.doc_label) return doc.doc_label;
  if (doc.bol_type === "CP_INDIVIDUAL") {
    const poDest = doc.po_dest ? `PO Dest ${doc.po_dest}` : "PO Dest";
    return `Individual (${poDest})`;
  }
  if (doc.bol_type === "CP_MASTER") return "Master (CP)";
  return doc.bol_type || "NORMAL";
};

const buildAddressLine = (parts) =>
  parts
    .map((value) => (value === undefined || value === null ? "" : String(value).trim()))
    .filter((value) => value)
    .join(", ");

const buildShipFromSummary = (shipFrom) => {
  if (!shipFrom) return "";
  const cityStateZip = buildAddressLine([shipFrom.city, shipFrom.state, shipFrom.zip]);
  return buildAddressLine([
    shipFrom.name || shipFrom.ship_point,
    shipFrom.address1,
    shipFrom.address2,
    cityStateZip,
    shipFrom.country,
    shipFrom.phone ? `Phone: ${shipFrom.phone}` : "",
  ]);
};

const buildShipToSummary = (shipTo) => {
  if (!shipTo) return "";
  const cityStateZip = buildAddressLine([shipTo.city, shipTo.state, shipTo.zip_code]);
  if (shipTo.address_line_1 || shipTo.address_line_2 || cityStateZip) {
    return buildAddressLine([shipTo.address_line_1, shipTo.address_line_2, cityStateZip]);
  }
  return buildAddressLine([shipTo.address]);
};

const buildMultilineSummary = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(", ");

const formatValue = (value) =>
  value === undefined || value === null ? "" : String(value);

const emptyCarrierLine = () => ({
  handling_units_text: "",
  packages: "",
  weight_lbs: "",
  cube: "",
  commodity_description: "",
  nmfc: "",
  freight_class: "",
});

const normalizeCarrierLine = (line) => {
  const safe = line || {};
  return {
    handling_units_text: safe.handling_units_text ?? "",
    packages: safe.packages ?? "",
    weight_lbs: safe.weight_lbs ?? "",
    cube: safe.cube ?? "",
    commodity_description: safe.commodity_description ?? "",
    nmfc: safe.nmfc ?? "",
    freight_class: safe.freight_class ?? "",
  };
};

const buildEditableKey = (doc) => {
  if (!doc) return "NORMAL";
  if (doc.editable_key) return doc.editable_key;
  if (doc.bol_type === "CP_INDIVIDUAL") {
    return `CP_INDIVIDUAL:${doc.po_dest || ""}`;
  }
  return doc.bol_type || "NORMAL";
};

const buildNewVersionPayload = (draft, saved) => {
  if (!draft || !saved) return draft;
  const editableKeys = [
    "trailer_number", "seal_number", "pro_number", "temperature_f", "temperature_mode",
    "pickup_date", "freight_terms", "freight_counted_by_driver", "commodity_description_override",
  ];
  const savedDocs = new Map((saved.bol_documents || []).map((doc) => [buildEditableKey(doc), doc]));
  return {
    ...draft,
    bol_documents: (draft.bol_documents || []).map((doc) => {
      const previous = savedDocs.get(buildEditableKey(doc));
      const fields = { ...(doc.editable_fields || {}) };
      editableKeys.forEach((key) => {
        if (previous?.editable_fields?.[key] !== undefined) fields[key] = previous.editable_fields[key];
      });
      // Older snapshots store the editable commodity in their first carrier line.
      if (!previous?.editable_fields?.commodity_description_override && previous) {
        const commodity = previous.carrier_information_lines?.[0]?.commodity_description ??
          previous.carrier_information?.commodity_description;
        if (commodity) fields.commodity_description_override = commodity;
      }
      return { ...doc, editable_fields: fields };
    }),
  };
};

const isEditableRoutingRowsDoc = (doc) => {
  const bolType = (doc?.bol_type || "NORMAL").trim();
  return bolType === "CP_MASTER" || bolType === "NORMAL";
};

const parseNumericValue = (value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const isBlankCarrierLine = (line) => {
  if (!line) return true;
  const handling = String(line.handling_units_text ?? "").trim();
  const packages = parseNumericValue(line.packages);
  const weight = parseNumericValue(line.weight_lbs);
  const cube = parseNumericValue(line.cube);
  const packagesEmpty = packages === null || packages === 0;
  const weightEmpty = weight === null || weight === 0;
  const cubeEmpty = cube === null || cube === 0;
  return !handling && packagesEmpty && weightEmpty && cubeEmpty;
};

const buildCarrierTotals = (lines) => {
  const totals = { packages: "", weight_lbs: "", cube: "" };
  let hasAny = false;
  ["packages", "weight_lbs", "cube"].forEach((field) => {
    let sum = 0;
    let hasField = false;
    lines.forEach((line) => {
      const parsed = parseNumericValue(line?.[field]);
      if (parsed !== null) {
        sum += parsed;
        hasField = true;
      }
    });
    if (hasField) {
      totals[field] = String(sum);
      hasAny = true;
    }
  });
  return { totals, allEmpty: !hasAny };
};


const normalizeLoadingMethod = (value) => {
  const text = value === undefined || value === null ? "" : String(value).trim().toUpperCase();
  if (text === "PALLETS") return "PALLET";
  if (text === "PALLET" || text === "FLOOR") return text;
  return "";
};

export const syncHandlingPlansAfterBolSave = async ({
  groupedPlans,
  headers,
  fetchImpl = fetch,
  apiBase = API_BASE,
}) => {
  const failures = [];
  for (const [sourceId, planRows] of Object.entries(groupedPlans || {})) {
    const groups = planRows.map((row, index) => ({
      ordinal: index + 1,
      loadingMethod: normalizeLoadingMethod(row.loading_method),
      handlingQuantity:
        normalizeLoadingMethod(row.loading_method) === "PALLET" ? row.pallets : row.floor,
      weightLbs: row.weight,
      allocations: row.handling_allocations || [],
    }));
    try {
      const planResp = await fetchImpl(
        `${apiBase}/api/admin/shipment-handling-plan/${sourceId}/activate`,
        { method: "POST", headers, body: JSON.stringify({ groups }) }
      );
      const planBody = await planResp.json().catch(() => ({}));
      if (!planResp.ok) {
        failures.push({
          sourceId,
          errorCode: planBody?.errorCode || "",
          error: planBody?.error || "Unable to save physical handling plan.",
        });
      }
    } catch (err) {
      failures.push({
        sourceId,
        errorCode: "",
        error: err?.message || "Unable to save physical handling plan.",
      });
    }
  }
  return failures;
};

export const saveBolVersionWithDeferredHandlingSync = async ({
  body,
  groupedPlans,
  headers,
  fetchImpl = fetch,
  apiBase = API_BASE,
}) => {
  const resp = await fetchImpl(`${apiBase}/api/admin/bol-version`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    return { resp, payload, handlingPlanFailures: [] };
  }
  const handlingPlanFailures = await syncHandlingPlansAfterBolSave({
    groupedPlans,
    headers,
    fetchImpl,
    apiBase,
  });
  return { resp, payload, handlingPlanFailures };
};

export const buildBolSaveMessage = (payload, handlingPlanFailures = []) => {
  const base = `Saved as Version ${payload?.version} (ID: ${payload?.bol_version_id})`;
  if (!handlingPlanFailures.length) return base;
  const onlyWaitingForFacts = handlingPlanFailures.every(
    (failure) => failure?.errorCode === "READY_SHIPMENT_FACTS_REQUIRED"
  );
  if (onlyWaitingForFacts) {
    return `${base}. Physical handling plan is pending until authoritative shipment facts are ready. This does not affect the BOL.`;
  }
  const firstError = handlingPlanFailures[0]?.error || "Unable to save physical handling plan.";
  return `${base}. Physical handling plan was not synchronized for ${handlingPlanFailures.length} PO(s): ${firstError} This does not affect the BOL.`;
};

const parseNonNegativeNumber = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const validateRoutingRow = (row) => {
  const method = normalizeLoadingMethod(row?.loading_method);
  if (!method) return "Loading Method must be PALLET or FLOOR.";
  const fieldLabels = {
    cases: "CASES",
    weight: "WEIGHT",
    cube: "CUBE",
    pallets: "PALLETS",
    floor: "FLOOR",
  };
  for (const field of Object.keys(fieldLabels)) {
    const value = parseNonNegativeNumber(row?.[field]);
    if (value === null || value < 0) {
      return `${fieldLabels[field]} must be a number >= 0.`;
    }
  }
  const pallets = parseNonNegativeNumber(row?.pallets) ?? 0;
  const floor = parseNonNegativeNumber(row?.floor) ?? 0;
  if (method === "PALLET") {
    if (pallets <= 0) return "Pallets must be > 0 when Loading Method is PALLET.";
    if (floor !== 0) return "Floor must be 0 when Loading Method is PALLET.";
  }
  if (method === "FLOOR") {
    if (floor <= 0) return "Floor must be > 0 when Loading Method is FLOOR.";
    if (pallets !== 0) return "Pallets must be 0 when Loading Method is FLOOR.";
  }
  return "";
};

const validateRoutingRows = (rows) => {
  const errors = {};
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const message = validateRoutingRow(row);
    if (message) errors[index] = message;
  });
  return errors;
};

const stripPoSuffix = (value) => {
  const text = value === undefined || value === null ? "" : String(value).trim();
  return text.replace(/-\d+$/, "");
};

const buildPoDisplay = (row) => {
  const base = String(row?.po_number_base ?? row?.po_number ?? "").trim();
  const suffix = row?.po_suffix;
  if (suffix === undefined || suffix === null || String(suffix).trim() === "") {
    return base;
  }
  return base ? `${base}-${suffix}` : "";
};

const buildCarrierLinesFromRows = (rows, commodityOverride) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const sumField = (list, field) =>
    list.reduce((total, row) => {
      const parsed = parseNumericValue(row?.[field]);
      return total + (parsed === null ? 0 : parsed);
    }, 0);

  const palletRows = safeRows.filter(
    (row) => normalizeLoadingMethod(row?.loading_method) === "PALLET"
  );
  const floorRows = safeRows.filter(
    (row) => normalizeLoadingMethod(row?.loading_method) === "FLOOR"
  );

  const lines = [];
  const palletCount = sumField(palletRows, "pallets");
  if (palletCount > 0) {
    lines.push({
      handling_units_text: `${Math.trunc(palletCount)} PALLET`,
      packages: sumField(palletRows, "cases"),
      weight_lbs: sumField(palletRows, "weight"),
      cube: sumField(palletRows, "cube"),
      commodity_description: "",
      nmfc: "",
      freight_class: "",
    });
  }

  const floorCases = sumField(floorRows, "cases");
  if (floorCases > 0) {
    lines.push({
      handling_units_text: `${Math.trunc(floorCases)} FLOOR`,
      packages: floorCases,
      weight_lbs: sumField(floorRows, "weight"),
      cube: sumField(floorRows, "cube"),
      commodity_description: "",
      nmfc: "",
      freight_class: "",
    });
  }

  if (!lines.length) {
    const totalPallets = sumField(safeRows, "pallets");
    const totalCases = sumField(safeRows, "cases");
    lines.push({
      handling_units_text: totalPallets > 0 ? `${Math.trunc(totalPallets)} Pallets` : `${Math.trunc(totalCases)} CTN`,
      packages: totalCases,
      weight_lbs: sumField(safeRows, "weight"),
      cube: sumField(safeRows, "cube"),
      commodity_description: "",
      nmfc: "",
      freight_class: "",
    });
  }

  const overrideText = commodityOverride !== undefined && commodityOverride !== null ? String(commodityOverride) : "";
  if (overrideText) {
    lines.forEach((line) => {
      line.commodity_description = overrideText;
    });
  }

  const normalized = lines.map(normalizeCarrierLine);
  while (normalized.length < 3) {
    normalized.push(emptyCarrierLine());
  }
  return normalized.slice(0, 3);
};

const cloneRoutingRows = (rows) =>
  Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [];

const allocationTotal = (allocations) =>
  (Array.isArray(allocations) ? allocations : []).reduce(
    (total, item) => total + (Number(item?.quantity) || 0), 0
  );

const rowsFromHandlingPlan = (sourceRow, plan) => {
  if (!plan || plan.effectiveStatus !== "ACTIVE" || !Array.isArray(plan.groups)) return [sourceRow];
  return plan.groups.map((group, index) => {
    const method = normalizeLoadingMethod(group.loadingMethod);
    const allocations = Array.isArray(group.allocations) ? group.allocations : [];
    return {
      ...sourceRow, po_number: plan.poNumber, po_number_base: plan.poNumber, po_suffix: index + 1,
      cases: allocationTotal(allocations), weight: group.weightLbs, cube: index === 0 ? sourceRow.cube : 0,
      pallets: method === "PALLET" ? group.handlingQuantity : 0,
      floor: method === "FLOOR" ? group.handlingQuantity : 0, loading_method: method,
      handling_allocations: allocations, handling_source_lines: plan.sourceLines || [],
      handling_plan_id: plan.id, handling_plan_version: plan.planVersion,
    };
  });
};

export const applyHandlingAuthoritiesToRows = (rows, authorities) => {
  const expanded = [];
  const seenPlans = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const authority = authorities?.[String(row?.routing_status_id || row?.id || "")];
    const plan = authority?.plan;
    if (plan?.id && !seenPlans.has(plan.id)) {
      seenPlans.add(plan.id);
      expanded.push(...rowsFromHandlingPlan(row, plan));
    } else if (!plan?.id) {
      expanded.push({ ...row, handling_source_lines: authority?.sourceLines || [] });
    }
  });
  return expanded;
};

const buildRoutingRowsMap = (payload) => {
  const map = {};
  const docs = Array.isArray(payload?.bol_documents) ? payload.bol_documents : [];
  docs.forEach((doc) => {
    const key = buildEditableKey(doc);
    map[key] = cloneRoutingRows(doc?.rows);
  });
  return map;
};

const normalizeRoutingRowForCompare = (row) => ({
  cases: parseNonNegativeNumber(row?.cases) ?? 0,
  weight: parseNonNegativeNumber(row?.weight) ?? 0,
  cube: parseNonNegativeNumber(row?.cube) ?? 0,
  pallets: parseNonNegativeNumber(row?.pallets) ?? 0,
  floor: parseNonNegativeNumber(row?.floor) ?? 0,
  loading_method: normalizeLoadingMethod(row?.loading_method) || "",
  po_suffix:
    row?.po_suffix === undefined || row?.po_suffix === null || String(row?.po_suffix).trim() === ""
      ? ""
      : String(row?.po_suffix).trim(),
  po_number_base: String(row?.po_number_base ?? "").trim(),
});

const areRoutingRowsEquivalent = (currentRows, originalRows) => {
  const current = Array.isArray(currentRows) ? currentRows : [];
  const original = Array.isArray(originalRows) ? originalRows : [];
  if (current.length !== original.length) return false;
  for (let index = 0; index < current.length; index += 1) {
    const left = normalizeRoutingRowForCompare(current[index]);
    const right = normalizeRoutingRowForCompare(original[index]);
    for (const key of Object.keys(left)) {
      if (left[key] !== right[key]) {
        return false;
      }
    }
  }
  return true;
};

const buildValidationErrorsMap = (rowsByDoc) => {
  const next = {};
  Object.entries(rowsByDoc || {}).forEach(([key, rows]) => {
    next[key] = validateRoutingRows(rows);
  });
  return next;
};

const COI_REASON_LABELS = {
  MISSING_ROUTING_X_POS_ROWS: "Routing X POs data not found for this load.",
  INVALID_ROW_PAYLOAD_JSON: "One or more Routing X POs payloads could not be parsed.",
  MISSING_ALLOC_KEYS: "No alloc-* keys were found in Routing X POs payloads.",
  MISSING_MRP_PRODUCTS_MAPPING: "Some Retail SKUs are missing MRP Products mappings.",
};

export default function BolPreviewModal({
  open,
  bolDraft,
  loading = false,
  selectedIndex = 0,
  routingStatusId = null,
  missingShipToNodes = [],
  forceDraftView = false,
  manualShipToOverrides: manualShipToOverridesProp = {},
  onManualShipToOverridesChange,
  onResolveMissingShipTo,
  onCancelMissingShipTo,
  onSelectIndex,
  onClose,
}) {
  const [draftPayload, setDraftPayload] = useState(null);
  const [activePayload, setActivePayload] = useState(null);
  const [mode, setMode] = useState("EDIT_DRAFT");
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersionMeta, setSelectedVersionMeta] = useState(null);
  const [editableByIndex, setEditableByIndex] = useState({});
  const [routingRowsByDocState, setRoutingRowsByDocState] = useState({});
  const [routingRowsByDocOriginal, setRoutingRowsByDocOriginal] = useState({});
  const [selectedRowIndexByDoc, setSelectedRowIndexByDoc] = useState({});
  const [validationErrorsByDoc, setValidationErrorsByDoc] = useState({});
  const [resetRoutingConfirmOpen, setResetRoutingConfirmOpen] = useState(false);
  const [resetRoutingDocKey, setResetRoutingDocKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [handlingAuthorities, setHandlingAuthorities] = useState({});
  const [savedVersion, setSavedVersion] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [missingShipToNodesState, setMissingShipToNodesState] = useState([]);
  const [manualShipToOverridesState, setManualShipToOverridesState] = useState({});
  const [manualShipToOpen, setManualShipToOpen] = useState(false);
  const [manualShipToError, setManualShipToError] = useState("");
  const [pickupDateShared, setPickupDateShared] = useState("");
  const [freightCountedByDriverShared, setFreightCountedByDriverShared] = useState(false);
  const [freightTermsShared, setFreightTermsShared] = useState("Collect");
  const [coiDetailsOpen, setCoiDetailsOpen] = useState(false);

  const docs = useMemo(
    () => (Array.isArray(activePayload?.bol_documents) ? activePayload.bol_documents : []),
    [activePayload]
  );
  const selectedDoc = docs[selectedIndex] || docs[0] || null;

  const latestVersion = versions.length ? versions[0] : null;

  const fetchVersions = async (loadId) => {
    if (!loadId) return [];
    setVersionsLoading(true);
    try {
      const user = getCurrentUser();
      const headers = {};
      if (user?.email) headers["X-User-Email"] = user.email;
      if (user?.role) headers["X-User-Role"] = user.role || "";
      const resp = await fetch(`${API_BASE}/api/admin/bol-versions/${loadId}`, { headers });
      if (!resp.ok) {
        if (resp.status === 404) return [];
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload?.error || "Unable to load saved versions.");
      }
      const payload = await resp.json().catch(() => ({}));
      return Array.isArray(payload?.versions) ? payload.versions : [];
    } finally {
      setVersionsLoading(false);
    }
  };

  const fetchVersionPayload = async (versionId) => {
    if (!versionId) return null;
    const user = getCurrentUser();
    const headers = {};
    if (user?.email) headers["X-User-Email"] = user.email;
    if (user?.role) headers["X-User-Role"] = user.role || "";
    const resp = await fetch(`${API_BASE}/api/admin/bol-version/${versionId}`, { headers });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(payload?.error || "Unable to load saved BOL.");
    }
    return payload;
  };

  const fetchHandlingAuthorities = async (payload) => {
    const payloadDocs = Array.isArray(payload?.bol_documents) ? payload.bol_documents : [];
    const ids = [...new Set(payloadDocs.flatMap((doc) => doc?.rows || [])
      .map((row) => Number(row?.routing_status_id || row?.id || 0)).filter(Boolean))];
    const user = getCurrentUser();
    const headers = {};
    const auth = getAuth();
    if (auth?.token) headers.Authorization = `Bearer ${auth.token}`;
    if (user?.email) headers["X-User-Email"] = user.email;
    if (user?.role) headers["X-User-Role"] = user.role || "";
    const entries = await Promise.all(ids.map(async (id) => {
      const resp = await fetch(`${API_BASE}/api/admin/shipment-handling-plan/${id}`, { headers });
      const body = await resp.json().catch(() => ({}));
      return [String(id), resp.ok ? body : { unavailable: true, error: body?.error }];
    }));
    return Object.fromEntries(entries);
  };

  useEffect(() => {
    if (!bolDraft) {
      setDraftPayload(null);
      setActivePayload(null);
      setEditableByIndex({});
      setRoutingRowsByDocState({});
      setRoutingRowsByDocOriginal({});
      setSelectedRowIndexByDoc({});
      setValidationErrorsByDoc({});
      setVersions([]);
      setMode("EDIT_DRAFT");
      setSelectedVersionId(null);
      setSelectedVersionMeta(null);
      setSavedVersion(null);
      setMissingShipToNodesState([]);
      setManualShipToOverridesState({});
      setManualShipToOpen(false);
      setManualShipToError("");
      setCoiDetailsOpen(false);
      setFreightTermsShared("Collect");
      return;
    }
    setDraftPayload(bolDraft);
    setActivePayload(bolDraft);
    setMode("EDIT_DRAFT");
    setSelectedVersionId(null);
    setSelectedVersionMeta(null);
    setSavedVersion(null);
    setMissingShipToNodesState([]);
    setManualShipToOverridesState({});
    setManualShipToOpen(false);
    setManualShipToError("");
    setCoiDetailsOpen(false);
    setFreightTermsShared("Collect");
    const initialRowsByDoc = buildRoutingRowsMap(bolDraft);
    setRoutingRowsByDocState(initialRowsByDoc);
    setRoutingRowsByDocOriginal(initialRowsByDoc);
    setValidationErrorsByDoc(buildValidationErrorsMap(initialRowsByDoc));
    setSelectedRowIndexByDoc({});
  }, [bolDraft]);

  useEffect(() => {
    if (!bolDraft) return undefined;
    let cancelled = false;
    fetchHandlingAuthorities(bolDraft).then((authorities) => {
      if (cancelled) return;
      setHandlingAuthorities(authorities);
      const hydrated = {};
      (bolDraft.bol_documents || []).forEach((doc) => {
        hydrated[buildEditableKey(doc)] = applyHandlingAuthoritiesToRows(doc?.rows, authorities);
      });
      setRoutingRowsByDocState(hydrated);
      setRoutingRowsByDocOriginal(hydrated);
      setValidationErrorsByDoc(buildValidationErrorsMap(hydrated));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [bolDraft]);

  useEffect(() => {
    if (!missingShipToNodes || missingShipToNodes.length === 0) return;
    const initialOverrides = { ...(manualShipToOverridesProp || {}) };
    missingShipToNodes.forEach((node) => {
      if (initialOverrides[node]) return;
      initialOverrides[node] = {
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip_code: "",
      };
    });
    setMissingShipToNodesState(missingShipToNodes);
    setManualShipToOverridesState(initialOverrides);
    setManualShipToError("");
    setManualShipToOpen(true);
  }, [missingShipToNodes, manualShipToOverridesProp]);

  useEffect(() => {
    if (!draftPayload?.load_id) return;
    let cancelled = false;
    const run = async () => {
      try {
        const list = await fetchVersions(draftPayload.load_id);
        if (cancelled) return;
        setVersions(list);
        if (forceDraftView) {
          setMode("EDIT_DRAFT");
          setSelectedVersionId(null);
          setSelectedVersionMeta(null);
          setActivePayload(draftPayload);
          return;
        }
        if (list.length) {
          const latest = list[0];
          setMode("VIEW_SAVED");
          setSelectedVersionId(latest.id);
          setSelectedVersionMeta(latest);
          const versionPayload = await fetchVersionPayload(latest.id);
          if (cancelled) return;
          setActivePayload(versionPayload?.payload || null);
          setSaveError("");
        } else {
          setMode("EDIT_DRAFT");
          setSelectedVersionId(null);
          setSelectedVersionMeta(null);
          setActivePayload(draftPayload);
        }
      } catch (err) {
        if (cancelled) return;
        setSaveError(err?.message || "Unable to load saved versions.");
        setMode("EDIT_DRAFT");
        setSelectedVersionId(null);
        setSelectedVersionMeta(null);
        setActivePayload(draftPayload);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [draftPayload, forceDraftView]);

  useEffect(() => {
    if (!activePayload) {
      setEditableByIndex({});
      setPickupDateShared("");
      setFreightCountedByDriverShared(false);
      setFreightTermsShared("Collect");
      return;
    }
    const next = {};
    const activeDocs = Array.isArray(activePayload?.bol_documents) ? activePayload.bol_documents : [];
    activeDocs.forEach((doc, index) => {
      const editableFields = { ...(doc?.editable_fields || {}) };
      if (
        editableFields.commodity_description_override === undefined ||
        String(editableFields.commodity_description_override || "").trim() === ""
      ) {
        const sourceLines = Array.isArray(doc?.carrier_information_lines) ? doc.carrier_information_lines : [];
        const sourceLine = sourceLines[0] || doc?.carrier_information || null;
        const sourceCommodity = sourceLine?.commodity_description;
        if (sourceCommodity !== undefined && sourceCommodity !== null && String(sourceCommodity).trim() !== "") {
          editableFields.commodity_description_override = sourceCommodity;
        }
      }
      next[index] = editableFields;
    });
    let nextPickupDate = "";
    let nextFreightCountedByDriver = null;
    let nextFreightTerms = "";
    if (mode === "EDIT_DRAFT") {
      activeDocs.forEach((doc) => {
        const value = (doc?.editable_fields?.pickup_date || "").trim();
        if (value) {
          nextPickupDate = value;
        }
        if (doc?.editable_fields?.freight_counted_by_driver !== undefined) {
          nextFreightCountedByDriver = Boolean(doc?.editable_fields?.freight_counted_by_driver);
        }
        if (doc?.editable_fields?.freight_terms) {
          const freightValue = String(doc.editable_fields.freight_terms).trim();
          if (doc?.bol_type === "CP_MASTER") {
            nextFreightTerms = freightValue;
          } else if (!nextFreightTerms) {
            nextFreightTerms = freightValue;
          }
        }
      });
      if (nextPickupDate) {
        activeDocs.forEach((_, index) => {
          next[index] = {
            ...(next[index] || {}),
            pickup_date: nextPickupDate,
          };
        });
      }
      if (nextFreightCountedByDriver !== null) {
        activeDocs.forEach((_, index) => {
          next[index] = {
            ...(next[index] || {}),
            freight_counted_by_driver: nextFreightCountedByDriver,
          };
        });
      }
      if (nextFreightTerms) {
        activeDocs.forEach((_, index) => {
          next[index] = {
            ...(next[index] || {}),
            freight_terms: nextFreightTerms,
          };
        });
      }
    }
    setEditableByIndex(next);
    setPickupDateShared(nextPickupDate);
    setFreightCountedByDriverShared(Boolean(nextFreightCountedByDriver));
    setFreightTermsShared(nextFreightTerms || "Collect");
    if (mode === "VIEW_SAVED" && activePayload) {
      const nextRowsByDoc = buildRoutingRowsMap(activePayload);
      setRoutingRowsByDocState(nextRowsByDoc);
      setValidationErrorsByDoc(buildValidationErrorsMap(nextRowsByDoc));
      setSelectedRowIndexByDoc({});
    }
  }, [activePayload, mode]);

  const currentDocKey = useMemo(() => buildEditableKey(selectedDoc), [selectedDoc]);
  const currentRoutingRows = useMemo(
    () =>
      routingRowsByDocState[currentDocKey] ||
      (Array.isArray(selectedDoc?.rows) ? selectedDoc.rows : []),
    [routingRowsByDocState, currentDocKey, selectedDoc]
  );
  const currentEditable = editableByIndex[selectedIndex] || selectedDoc?.editable_fields || {};
  const commodityOverride = currentEditable.commodity_description_override || "";
  const currentCarrierLines = useMemo(
    () => buildCarrierLinesFromRows(currentRoutingRows, commodityOverride),
    [currentRoutingRows, commodityOverride]
  );
  const carrierTotals = useMemo(
    () => buildCarrierTotals(currentCarrierLines),
    [currentCarrierLines]
  );
  const currentRowSelection = selectedRowIndexByDoc[currentDocKey] ?? null;
  const isEditableRoutingRows = useMemo(
    () => isEditableRoutingRowsDoc(selectedDoc),
    [selectedDoc]
  );
  const editableRoutingRowDocKeys = useMemo(
    () => docs.filter((doc) => isEditableRoutingRowsDoc(doc)).map((doc) => buildEditableKey(doc)),
    [docs]
  );
  const currentRowErrors = isEditableRoutingRows ? (validationErrorsByDoc[currentDocKey] || {}) : {};
  const hasValidationErrors = useMemo(
    () =>
      editableRoutingRowDocKeys.some((key) =>
        Object.values(validationErrorsByDoc[key] || {}).some(Boolean)
      ),
    [editableRoutingRowDocKeys, validationErrorsByDoc]
  );
  const routingRowsModifiedByDoc = useMemo(() => {
    const result = {};
    const keys = new Set([
      ...Object.keys(routingRowsByDocOriginal || {}),
      ...Object.keys(routingRowsByDocState || {}),
    ]);
    keys.forEach((key) => {
      result[key] = !areRoutingRowsEquivalent(
        routingRowsByDocState[key] || [],
        routingRowsByDocOriginal[key] || []
      );
    });
    return result;
  }, [routingRowsByDocOriginal, routingRowsByDocState]);
  const isCurrentRoutingRowsModified = Boolean(routingRowsModifiedByDoc[currentDocKey]);
  const hasSplitApplied = useMemo(
    () =>
      currentRoutingRows.some((row) => {
        const suffix = row?.po_suffix;
        const base = row?.po_number_base;
        return (
          (suffix !== undefined && suffix !== null && String(suffix).trim() !== "") ||
          (base !== undefined && base !== null && String(base).trim() !== "")
        );
      }),
    [currentRoutingRows]
  );
  const shipFromSummary = useMemo(() => buildShipFromSummary(bolDraft?.ship_from), [bolDraft]);
  const shipToSummary = useMemo(() => buildShipToSummary(selectedDoc?.ship_to), [selectedDoc]);
  const thirdPartyBillToSummary = useMemo(
    () => buildMultilineSummary(selectedDoc?.third_party_freight_bill_to_text),
    [selectedDoc]
  );
  const rows = currentRoutingRows;
  const firstRow = rows[0] || {};
  const headerCarrierName = selectedDoc?.header_carrier_name || firstRow.carrier_name || "";
  const displayedBolNumber = selectedDoc?.bol_number || bolDraft?.load_id || "";
  const poList = Array.isArray(selectedDoc?.po_list) ? selectedDoc.po_list : [];
  const isMasterBol = selectedDoc?.bol_type === "CP_MASTER";
  const masterCoiRows = Array.isArray(selectedDoc?.customer_order_information_master)
    ? selectedDoc.customer_order_information_master
    : [];
  const coiRows =
    !isMasterBol && Array.isArray(selectedDoc?.customer_order_information) && selectedDoc.customer_order_information.length > 0
      ? selectedDoc.customer_order_information
      : !isMasterBol && Array.isArray(activePayload?.customer_order_information)
        ? activePayload.customer_order_information
        : [];
  const coiDiagnostics = activePayload?.diagnostics?.coi;
  const coiIncomplete = coiDiagnostics?.coi_status === "incomplete";
  const coiReasons = Array.isArray(coiDiagnostics?.reasons) ? coiDiagnostics.reasons : [];
  const missingRetailSkus = Array.isArray(coiDiagnostics?.details?.missing_retail_skus)
    ? coiDiagnostics.details.missing_retail_skus
    : [];
  const reasonLabels = coiReasons.map((reason) => COI_REASON_LABELS[reason] || reason);

  const isViewingSaved = mode === "VIEW_SAVED";
  const canEditRoutingRows = !isViewingSaved && isEditableRoutingRows;
  const activeVersionBadge = selectedVersionMeta
    ? `Version v${selectedVersionMeta.version}${latestVersion?.id === selectedVersionMeta.id ? " (Latest)" : ""}`
    : "";

  const handleSelectVersion = async (versionItem) => {
    if (!versionItem?.id || versionsLoading) return;
    setSaveError("");
    try {
      setMode("VIEW_SAVED");
      setSelectedVersionId(versionItem.id);
      setSelectedVersionMeta(versionItem);
      const versionPayload = await fetchVersionPayload(versionItem.id);
      setActivePayload(versionPayload?.payload || null);
      setSaveMessage("");
    } catch (err) {
      setSaveError(err?.message || "Unable to load saved BOL.");
    }
  };

  const handleCreateNewVersion = () => {
    const basePayload = buildNewVersionPayload(draftPayload, isViewingSaved ? activePayload : null);
    if (!basePayload) return;
    setMode("EDIT_DRAFT");
    setActivePayload(basePayload);
    setSelectedVersionId(null);
    setSelectedVersionMeta(null);
    setSaveMessage("");
    setSaveError("");
    const initialRowsByDoc = {};
    (basePayload.bol_documents || []).forEach((doc) => {
      initialRowsByDoc[buildEditableKey(doc)] = applyHandlingAuthoritiesToRows(doc?.rows, handlingAuthorities);
    });
    setRoutingRowsByDocState(initialRowsByDoc);
    setRoutingRowsByDocOriginal(initialRowsByDoc);
    setValidationErrorsByDoc(buildValidationErrorsMap(initialRowsByDoc));
    setSelectedRowIndexByDoc({});
  };

  const submitFinalize = async (overrides = null) => {
    if (saving || !routingStatusId || !draftPayload) return;
    setSaveError("");
    setSaveMessage("");
    setSavedVersion(null);
    setSaving(true);
    try {
      const user = getCurrentUser();
      const headers = { "Content-Type": "application/json" };
      const auth = getAuth();
      if (auth?.token) headers.Authorization = `Bearer ${auth.token}`;
      if (user?.email) headers["X-User-Email"] = user.email;
      if (user?.role) headers["X-User-Role"] = user.role || "";
      const editableFieldsByDoc = {};
      const carrierInformationByDoc = {};
      const carrierInformationByDocFlat = {};
      const routingRowsByDoc = {};
      docs.forEach((doc, index) => {
        const key = buildEditableKey(doc);
        const editableFields = editableByIndex[index] || doc?.editable_fields || {};
        editableFieldsByDoc[key] = editableFields;
        if (isEditableRoutingRowsDoc(doc)) {
          const rows = routingRowsByDocState[key] || (Array.isArray(doc?.rows) ? doc.rows : []);
          routingRowsByDoc[key] = rows;
        }
        const commodityOverride = editableFields?.commodity_description_override;
        if (commodityOverride !== undefined) {
          carrierInformationByDoc[key] = {
            commodity_description: commodityOverride,
            lines: [{ commodity_description: commodityOverride }],
          };
          carrierInformationByDocFlat[key] = { commodity_description: commodityOverride };
        }
      });
      const body = {
        routing_status_id: routingStatusId,
        routing_rows_by_doc: routingRowsByDoc,
        editable_fields_by_doc: editableFieldsByDoc,
        carrier_information_by_doc: carrierInformationByDoc,
        carrier_information_by_doc_flat: carrierInformationByDocFlat,
        note: draftPayload?.note ?? "",
      };
      const authorityRows = routingRowsByDoc.CP_MASTER || Object.values(routingRowsByDoc)[0] || [];
      const groupedPlans = {};
      authorityRows.forEach((row) => {
        const sourceId = Number(row?.routing_status_id || 0);
        if (!sourceId || (!row?.po_number_base && !row?.po_suffix)) return;
        groupedPlans[sourceId] = groupedPlans[sourceId] || [];
        groupedPlans[sourceId].push(row);
      });
      const resolvedOverrides = overrides || manualShipToOverridesProp || manualShipToOverridesState;
      if (resolvedOverrides && Object.keys(resolvedOverrides).length) {
        body.manual_ship_to_overrides = resolvedOverrides;
        if (process.env.NODE_ENV !== "production") {
          console.info("BOL finalize overrides", { routingStatusId, overrides: resolvedOverrides });
        }
      }
      const { resp, payload, handlingPlanFailures } = await saveBolVersionWithDeferredHandlingSync({
        body,
        groupedPlans,
        headers,
        fetchImpl: fetch,
        apiBase: API_BASE,
      });
      if (!resp.ok) {
        if (payload?.errorCode === "BOL_ROUTING_MEMBERSHIP_MISMATCH") {
          throw new Error("BOL membership changed. Close and reopen the BOL preview before creating a new version.");
        }
        if (payload?.error === "MISSING_SHIP_TO_ADDRESS" && Array.isArray(payload?.missing_address_nodes)) {
          const nodes = payload.missing_address_nodes.filter((node) => node);
          const initialOverrides = { ...(manualShipToOverridesProp || {}), ...(manualShipToOverridesState || {}) };
          nodes.forEach((node) => {
            if (initialOverrides[node]) return;
            initialOverrides[node] = {
              address_line_1: "",
              address_line_2: "",
              city: "",
              state: "",
              zip_code: "",
            };
          });
          setMissingShipToNodesState(nodes);
          setManualShipToOverridesState(initialOverrides);
          if (onManualShipToOverridesChange) {
            onManualShipToOverridesChange(initialOverrides);
          }
          setManualShipToError("");
          setManualShipToOpen(true);
          return;
        }
        throw new Error(payload?.error || "Unable to save BOL version.");
      }
      const nextVersion = {
        bol_version_id: payload?.bol_version_id,
        load_id: payload?.load_id,
        version: payload?.version,
      };
      setSavedVersion(nextVersion);
      setSaveMessage(buildBolSaveMessage(payload, handlingPlanFailures));
      if (draftPayload?.load_id) {
        const list = await fetchVersions(draftPayload.load_id);
        setVersions(list);
        if (list.length) {
          const latest = list[0];
          setMode("VIEW_SAVED");
          setSelectedVersionId(latest.id);
          setSelectedVersionMeta(latest);
          const versionPayload = await fetchVersionPayload(latest.id);
          setActivePayload(versionPayload?.payload || null);
        }
      }
    } catch (err) {
      setSaveError(err?.message || "Unable to save BOL version.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = () => {
    submitFinalize();
  };

  const handleDownloadPdf = async () => {
    const versionId = selectedVersionId || savedVersion?.bol_version_id;
    if (!versionId || downloading) return;
    setSaveError("");
    setDownloading(true);
    try {
      const user = getCurrentUser();
      const headers = {};
      if (user?.email) headers["X-User-Email"] = user.email;
      if (user?.role) headers["X-User-Role"] = user.role || "";
      const resp = await fetch(
        `${API_BASE}/api/admin/bol-version/${versionId}/pdf`,
        { headers }
      );
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload?.error || "Unable to download PDF.");
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileLoadId = selectedVersionMeta?.load_id || savedVersion?.load_id || activePayload?.load_id || "draft";
      const fileVersion = selectedVersionMeta?.version || savedVersion?.version || "";
      const fallbackName = `BOL_${fileLoadId}_v${fileVersion}.pdf`;
      link.href = url;
      link.download = fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSaveError(err?.message || "Unable to download PDF.");
    } finally {
      setDownloading(false);
    }
  };


  const updateRoutingRowsForDoc = (docKey, nextRows) => {
    setRoutingRowsByDocState((prev) => ({ ...prev, [docKey]: nextRows }));
    setValidationErrorsByDoc((prev) => ({ ...prev, [docKey]: validateRoutingRows(nextRows) }));
  };

  const handleRoutingRowChange = (rowIndex, field, value) => {
    const nextRows = [...(currentRoutingRows || [])];
    const nextRow = { ...(nextRows[rowIndex] || {}) };
    let nextValue = value;
    if (["cases", "weight", "cube", "pallets", "floor"].includes(field)) {
      const parsed = parseNonNegativeNumber(value);
      nextValue = parsed === null ? value : parsed;
    }
    if (field === "loading_method") {
      nextValue = normalizeLoadingMethod(value) || value;
    }
    nextRow[field] = nextValue;
    nextRows[rowIndex] = nextRow;
    updateRoutingRowsForDoc(currentDocKey, nextRows);
  };

  const handleAllocationChange = (rowIndex, sourceLine, value) => {
    const nextRows = [...(currentRoutingRows || [])];
    const row = { ...(nextRows[rowIndex] || {}) };
    const allocations = [...(row.handling_allocations || [])];
    const lineId = Number(sourceLine.ediPo850LineId);
    const existing = allocations.findIndex((item) => Number(item.ediPo850LineId) === lineId);
    const quantity = Number(value);
    if (!value || !Number.isFinite(quantity) || quantity <= 0) {
      if (existing >= 0) allocations.splice(existing, 1);
    } else {
      const item = { ediPo850LineId: lineId, quantity, uom: sourceLine.uom };
      if (existing >= 0) allocations[existing] = item; else allocations.push(item);
    }
    row.handling_allocations = allocations;
    nextRows[rowIndex] = row;
    updateRoutingRowsForDoc(currentDocKey, nextRows);
  };

  const handleSplitPo = () => {
    if (!canEditRoutingRows) return;
    setSaveError("");
    if (currentRowSelection === null || currentRowSelection === undefined) {
      setSaveError("Select a routing row to split.");
      return;
    }
    const rowsCopy = [...(currentRoutingRows || [])];
    const target = rowsCopy[currentRowSelection];
    if (!target) {
      setSaveError("Select a routing row to split.");
      return;
    }
    const base = stripPoSuffix(target.po_number_base || target.po_number || "");
    const normalizedBase = base || "";
    const first = {
      ...target,
      po_number: normalizedBase,
      po_number_base: normalizedBase,
      po_suffix: 1,
      handling_allocations: [],
    };
    const second = {
      ...target,
      po_number: normalizedBase,
      po_number_base: normalizedBase,
      po_suffix: 2,
      handling_allocations: [],
    };
    rowsCopy[currentRowSelection] = first;
    rowsCopy.splice(currentRowSelection + 1, 0, second);
    updateRoutingRowsForDoc(currentDocKey, rowsCopy);
    setSelectedRowIndexByDoc((prev) => ({ ...prev, [currentDocKey]: currentRowSelection }));
  };

  const handleResetRoutingRows = () => {
    if (!canEditRoutingRows) return;
    setResetRoutingDocKey(currentDocKey);
    setResetRoutingConfirmOpen(true);
  };

  const handleConfirmResetRoutingRows = () => {
    const targetKey = resetRoutingDocKey || currentDocKey;
    const originalRows = routingRowsByDocOriginal[targetKey] || [];
    updateRoutingRowsForDoc(targetKey, cloneRoutingRows(originalRows));
    setSelectedRowIndexByDoc((prev) => ({ ...prev, [targetKey]: null }));
    setResetRoutingConfirmOpen(false);
    setResetRoutingDocKey(null);
  };

  const handleCancelResetRoutingRows = () => {
    setResetRoutingConfirmOpen(false);
    setResetRoutingDocKey(null);
  };

  const handleOverrideChange = (node, field, value) => {
    setManualShipToOverridesState((prev) => ({
      ...prev,
      [node]: {
        ...(prev[node] || {}),
        [field]: value,
      },
    }));
  };

  const handleConfirmOverrides = () => {
    const normalized = {};
    const missing = [];
    const useDraftResolve = Boolean(onResolveMissingShipTo && missingShipToNodes?.length);
    missingShipToNodesState.forEach((node) => {
      const entry = manualShipToOverridesState[node] || {};
      const addressLine1 = (entry.address_line_1 || "").trim();
      const addressLine2 = (entry.address_line_2 || "").trim();
      const city = (entry.city || "").trim();
      const state = (entry.state || "").trim();
      const zipCode = (entry.zip_code || "").trim();
      if (!addressLine1 || !city || !state || !zipCode) {
        missing.push(node);
        return;
      }
      normalized[node] = {
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        city,
        state,
        zip_code: zipCode,
      };
    });
    if (missing.length > 0) {
      setManualShipToError("Please complete all required Ship To fields.");
      return;
    }
    setManualShipToOpen(false);
    setManualShipToError("");
    const mergedOverrides = { ...(manualShipToOverridesProp || {}), ...(manualShipToOverridesState || {}), ...normalized };
    setManualShipToOverridesState(mergedOverrides);
    if (onManualShipToOverridesChange) {
      onManualShipToOverridesChange(mergedOverrides);
    }
    if (useDraftResolve) {
      onResolveMissingShipTo(mergedOverrides);
      return;
    }
    submitFinalize(mergedOverrides);
  };

  const handleCancelOverrides = () => {
    setManualShipToOpen(false);
    setManualShipToError("");
    if (onCancelMissingShipTo && missingShipToNodes?.length) {
      onCancelMissingShipTo();
      return;
    }
    setSaveError("BOL version was not created.");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex w-64 flex-col border-r border-slate-200 bg-slate-50">
          <div className="border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-700">Documents</h3>
          </div>
          <div className="flex-1 overflow-auto">
            {versions.length > 0 && (
              <div className="border-b border-slate-200">
                <div className="px-4 py-2 text-xs font-semibold uppercase text-slate-400">Versions</div>
                {versions.map((versionItem) => {
                  const active = versionItem.id === selectedVersionId && mode === "VIEW_SAVED";
                  return (
                    <button
                      key={`version-${versionItem.id}`}
                      type="button"
                      onClick={() => handleSelectVersion(versionItem)}
                      className={`flex w-full flex-col items-start gap-1 border-t border-slate-200 px-4 py-2 text-left text-xs ${
                        active ? "bg-white font-semibold text-emerald-700" : "text-slate-600 hover:bg-white"
                      }`}
                    >
                      <span>v{versionItem.version}</span>
                      <span className="text-[10px] text-slate-400">
                        {versionItem.created_by_email || "unknown"} {versionItem.created_at || ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {versions.length === 0 && (
              <div className="border-b border-slate-200 px-4 py-3 text-xs text-slate-400">
                No saved versions yet.
              </div>
            )}
            {docs.map((doc, index) => {
              const active = index === selectedIndex;
              return (
                <button
                  key={`${doc?.bol_type || "doc"}-${index}`}
                  type="button"
                  onClick={() => onSelectIndex(index)}
                  className={`flex w-full flex-col items-start gap-1 border-b border-slate-200 px-4 py-3 text-left text-sm ${
                    active ? "bg-white font-semibold text-emerald-700" : "text-slate-600 hover:bg-white"
                  }`}
                >
                  <span>{buildDocLabel(doc)}</span>
                  {doc?.po_list?.length ? (
                    <span className="text-xs text-slate-400">{doc.po_list.length} POs</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-1 flex-col">
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-800">BOL Preview</h2>
              <p className="text-sm text-slate-500">Review draft data before finalize.</p>
              {activeVersionBadge && (
                <span className="mt-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                  {activeVersionBadge}
                </span>
              )}
              {!isViewingSaved && isEditableRoutingRows && isCurrentRoutingRowsModified && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <div className="font-semibold">Routing Rows Modified</div>
                  <div className="text-[11px] text-amber-700">Totals and COI will reflect your edits after Finalize.</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(selectedVersionId || savedVersion?.bol_version_id) && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="rounded-md border border-emerald-200 px-3 py-1.5 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloading ? "Downloading..." : "Download PDF"}
                </button>
              )}
              {isViewingSaved && (
                <button
                  type="button"
                  onClick={handleCreateNewVersion}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Create New Version
                </button>
              )}
              <button
                type="button"
                onClick={handleFinalize}
                disabled={saving || !routingStatusId || isViewingSaved || hasValidationErrors}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Finalize"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
              >
                Close
              </button>
            </div>
          </div>

          <div className="relative flex-1 overflow-auto px-6 py-5">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                <div className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow">
                  Loading...
                </div>
              </div>
            )}
            {!selectedDoc && !loading && (
              <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                No draft data available.
              </div>
            )}

            {saveError && (
              <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {saveError}
              </div>
            )}
            {saveMessage && (
              <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                {saveMessage}
              </div>
            )}
            {coiIncomplete && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="font-semibold">COI incomplete</div>
                <p className="mt-1 text-amber-800">
                  Some customer order information is missing or could not be derived from Routing X POs or MRP Products.
                  You can still save or download the PDF.
                </p>
                {reasonLabels.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-800">
                    {reasonLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                )}
                {missingRetailSkus.length > 0 && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setCoiDetailsOpen((prev) => !prev)}
                      className="text-xs font-semibold text-amber-900 underline decoration-amber-300"
                    >
                      {coiDetailsOpen ? "Hide missing Retail SKUs" : "Show missing Retail SKUs"}
                    </button>
                    {coiDetailsOpen && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-amber-900">
                        {missingRetailSkus.slice(0, 20).map((sku) => (
                          <span key={sku} className="rounded-full border border-amber-300 bg-white px-2 py-0.5">
                            {sku}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {selectedDoc && (
              <div className="space-y-6">
                <section className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="grid gap-4 text-sm text-slate-700 md:grid-cols-2">
                    <div>
                      <div className="text-xs uppercase text-slate-400">BOL Number</div>
                      <div className="font-semibold">{formatValue(displayedBolNumber)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">BOL Type</div>
                      <div className="font-semibold">{buildDocLabel(selectedDoc)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Ship From</div>
                      <div className="font-medium">{shipFromSummary || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Ship To</div>
                      <div className="font-medium">{shipToSummary || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Carrier Name</div>
                      <div className="font-medium">{formatValue(headerCarrierName) || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">SCAC</div>
                      <div className="font-medium">{formatValue(firstRow.industry_scac) || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Third Party Bill To</div>
                      <div className="font-medium">{thirdPartyBillToSummary || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Load Dest Type</div>
                      <div className="font-medium">{formatValue(firstRow.load_dest_type) || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-400">Load Dest</div>
                      <div className="font-medium">{formatValue(firstRow.load_dest) || "-"}</div>
                    </div>
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="text-sm font-semibold text-slate-700">PO List</div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                    {poList.length
                      ? poList.map((po) => (
                          <span key={po} className="rounded-full border border-slate-200 bg-white px-2 py-1">
                            {po}
                          </span>
                        ))
                      : "-"}
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-semibold text-slate-700">Routing Rows</div>
                      {isEditableRoutingRows && hasSplitApplied && (
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                            PO Split Applied
                          </span>
                          <span className="text-[11px] text-slate-400">Split affects COI and totals.</span>
                        </div>
                      )}
                    </div>
                    {isEditableRoutingRows && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSplitPo}
                          disabled={!canEditRoutingRows || !rows.length}
                          className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          SPLIT PO
                        </button>
                        <button
                          type="button"
                          onClick={handleResetRoutingRows}
                          disabled={!canEditRoutingRows || !rows.length}
                          className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Reset Routing Rows
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-xs text-slate-700">
                      <thead className="bg-slate-100 text-[11px] uppercase text-slate-500">
                        <tr>
                          <th className="px-2 py-2 text-center">Select</th>
                          <th className="px-3 py-2 text-left">PO Number</th>
                          <th className="px-3 py-2 text-left">PO Dest</th>
                          <th className="px-3 py-2 text-left">Outbound Code</th>
                          <th className="px-3 py-2 text-right">Cases</th>
                          <th className="px-3 py-2 text-right">Weight</th>
                          <th className="px-3 py-2 text-right">Cube</th>
                          <th className="px-3 py-2 text-right">Pallets</th>
                          <th className="px-3 py-2 text-right">Floor</th>
                          <th className="px-3 py-2 text-left">Loading Method</th>
                          <th className="px-3 py-2 text-left">MABD</th>
                          <th className="px-3 py-2 text-left">Carrier PU Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length ? (
                          rows.map((row, rowIndex) => (
                            <React.Fragment
                              key={`${row.routing_status_id || row.po_number || "row"}-${rowIndex}`}
                            >
                              <tr
                                className={`border-t ${
                                  currentRowErrors[rowIndex] ? "border-rose-200 bg-rose-50/40" : "border-slate-100"
                                }`}
                              >
                                <td className="px-2 py-2 text-center">
                                  <input
                                    type="radio"
                                    name={`routing-row-select-${currentDocKey}`}
                                    checked={currentRowSelection === rowIndex}
                                    disabled={!canEditRoutingRows}
                                    onChange={() =>
                                      setSelectedRowIndexByDoc((prev) => ({ ...prev, [currentDocKey]: rowIndex }))
                                    }
                                    className="h-3.5 w-3.5 rounded-full border border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                  />
                                </td>
                                <td className="px-3 py-2">{formatValue(buildPoDisplay(row))}</td>
                                <td className="px-3 py-2">{formatValue(row.po_dest)}</td>
                                <td className="px-3 py-2">{formatValue(row.outbound_code)}</td>
                                <td className="px-3 py-2 text-right">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.cases)
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={row.cases ?? ""}
                                      onChange={(e) => handleRoutingRowChange(rowIndex, "cases", e.target.value)}
                                      className="w-[3.75rem] rounded-md border border-slate-300 px-2 py-1 text-right text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.weight)
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={row.weight ?? ""}
                                      onChange={(e) => handleRoutingRowChange(rowIndex, "weight", e.target.value)}
                                      className="w-[4.5rem] rounded-md border border-slate-300 px-2 py-1 text-right text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.cube)
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={row.cube ?? ""}
                                      onChange={(e) => handleRoutingRowChange(rowIndex, "cube", e.target.value)}
                                      className="w-[3.75rem] rounded-md border border-slate-300 px-2 py-1 text-right text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.pallets)
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={row.pallets ?? ""}
                                      onChange={(e) => handleRoutingRowChange(rowIndex, "pallets", e.target.value)}
                                      className="w-[3.75rem] rounded-md border border-slate-300 px-2 py-1 text-right text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.floor)
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={row.floor ?? ""}
                                      onChange={(e) => handleRoutingRowChange(rowIndex, "floor", e.target.value)}
                                      className="w-[3.75rem] rounded-md border border-slate-300 px-2 py-1 text-right text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {!canEditRoutingRows ? (
                                    formatValue(row.loading_method)
                                  ) : (
                                    <select
                                      value={normalizeLoadingMethod(row.loading_method) || ""}
                                      onChange={(e) =>
                                        handleRoutingRowChange(rowIndex, "loading_method", e.target.value)
                                      }
                                      className="w-[5.25rem] rounded-md border border-slate-300 px-2 py-1 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    >
                                      <option value="">Select</option>
                                      <option value="PALLET">PALLET</option>
                                      <option value="FLOOR">FLOOR</option>
                                    </select>
                                  )}
                                </td>
                                <td className="px-3 py-2">{formatValue(row.mabd)}</td>
                                <td className="px-3 py-2">{formatValue(row.carrier_pu_date)}</td>
                              </tr>
                              {currentRowErrors[rowIndex] && (
                                <tr className="border-t border-rose-100 bg-rose-50">
                                  <td colSpan={12} className="px-3 py-2 text-[11px] text-rose-600">
                                    {currentRowErrors[rowIndex]}
                                  </td>
                                </tr>
                              )}
                              {(row.po_number_base || row.po_suffix) && Array.isArray(row.handling_source_lines) && (
                                <tr className="border-t border-slate-100 bg-slate-50/70">
                                  <td colSpan={12} className="px-3 py-2">
                                    <div className="text-[11px] font-semibold text-slate-600">Shipped PO line allocation</div>
                                    <div className="mt-2 flex flex-wrap gap-3">
                                      {row.handling_source_lines.map((line) => {
                                        const assigned = (row.handling_allocations || []).find(
                                          (item) => Number(item.ediPo850LineId) === Number(line.ediPo850LineId)
                                        );
                                        return (
                                          <label key={line.ediPo850LineId} className="flex items-center gap-2 text-[11px] text-slate-600">
                                            <span>Line {line.lineNumber} · {line.vendorSku} · shipped {line.shippedQuantity} {line.uom}</span>
                                            <input type="number" min="0" step="1" value={assigned?.quantity ?? ""}
                                              disabled={!canEditRoutingRows}
                                              aria-label={`Allocate line ${line.lineNumber} to ${buildPoDisplay(row)}`}
                                              onChange={(event) => handleAllocationChange(rowIndex, line, event.target.value)}
                                              className="w-16 rounded-md border border-slate-300 px-2 py-1 text-right" />
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <tr>
                            <td className="px-3 py-3 text-slate-400" colSpan={12}>
                              No rows available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="text-sm font-semibold text-slate-700">Customer Order Information</div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-xs text-slate-700">
                      <thead className="bg-slate-100 text-[11px] uppercase text-slate-500">
                        {isMasterBol ? (
                          <tr>
                            <th className="px-3 py-2 text-left">Customer Order No</th>
                            <th className="px-3 py-2 text-right"># Packages</th>
                            <th className="px-3 py-2 text-right">Weight</th>
                            <th className="px-3 py-2 text-center">Pallet/Slip</th>
                            <th className="px-3 py-2 text-left">Additional Shipper Information</th>
                          </tr>
                        ) : (
                          <tr>
                            <th className="px-3 py-2 text-left">Customer Order No</th>
                            <th className="px-3 py-2 text-right">Packages</th>
                            <th className="px-3 py-2 text-right">Unit Weight Lbs</th>
                            <th className="px-3 py-2 text-right">Weight Lbs</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {isMasterBol ? (
                          masterCoiRows.length ? (
                            masterCoiRows.map((row, index) => (
                              <tr key={`${row.customer_order_no || "coi"}-${index}`} className="border-t border-slate-100">
                                <td className="px-3 py-2">{formatValue(row.customer_order_no)}</td>
                                <td className="px-3 py-2 text-right">{formatValue(row.packages)}</td>
                                <td className="px-3 py-2 text-right">{formatValue(row.weight_lbs)}</td>
                                <td className="px-3 py-2 text-center">{formatValue(row.pallet_slip)}</td>
                                <td className="px-3 py-2">{formatValue(row.additional_shipper_info)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="px-3 py-3 text-slate-400" colSpan={5}>
                                No COI rows available.
                              </td>
                            </tr>
                          )
                        ) : (
                          coiRows.length ? (
                            coiRows.map((row, index) => (
                              <tr key={`${row.customer_order_no || "coi"}-${index}`} className="border-t border-slate-100">
                                <td className="px-3 py-2">{formatValue(row.customer_order_no)}</td>
                                <td className="px-3 py-2 text-right">{formatValue(row.packages)}</td>
                                <td className="px-3 py-2 text-right">{formatValue(row.unit_weight_lbs)}</td>
                                <td className="px-3 py-2 text-right">{formatValue(row.weight_lbs)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="px-3 py-3 text-slate-400" colSpan={4}>
                                No COI rows available.
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Editable Fields</div>
                  <div className="grid gap-4 md:grid-cols-3">
                    {[
                      { key: "trailer_number", label: "Trailer Number" },
                      { key: "seal_number", label: "Seal Number" },
                      { key: "pro_number", label: "PRO Number" },
                      { key: "temperature_f", label: "Temperature F" },
                      { key: "temperature_mode", label: "Temperature Mode" },
                      { key: "freight_terms", label: "Freight Terms", type: "select" },
                      { key: "pickup_date", label: "Pickup Date", type: "date" },
                    ].map((field) => (
                      <label key={field.key} className="text-xs text-slate-600">
                        {field.label}
                        {field.type === "select" ? (
                          <select
                            value={freightTermsShared || currentEditable[field.key] || "Collect"}
                            disabled={isViewingSaved}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setFreightTermsShared(nextValue);
                              setEditableByIndex((prev) => ({
                                ...prev,
                                ...docs.reduce((acc, _, index) => {
                                  acc[index] = {
                                    ...(prev[index] || {}),
                                    [field.key]: nextValue,
                                  };
                                  return acc;
                                }, {}),
                              }));
                            }}
                            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          >
                            {["Prepaid", "Collect", "3rd Party"].map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type || "text"}
                            value={
                              field.key === "pickup_date"
                                ? pickupDateShared || currentEditable[field.key] || ""
                                : currentEditable[field.key] || ""
                            }
                            disabled={isViewingSaved}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              if (field.key === "pickup_date") {
                                setPickupDateShared(nextValue);
                                setEditableByIndex((prev) => {
                                  const updated = { ...prev };
                                  Object.keys(updated).forEach((key) => {
                                    updated[key] = {
                                      ...(updated[key] || {}),
                                      pickup_date: nextValue,
                                    };
                                  });
                                  if (!updated[selectedIndex]) {
                                    updated[selectedIndex] = { pickup_date: nextValue };
                                  }
                                  return updated;
                                });
                                return;
                              }
                              setEditableByIndex((prev) => ({
                                ...prev,
                                [selectedIndex]: {
                                  ...(prev[selectedIndex] || {}),
                                  [field.key]: nextValue,
                                },
                              }));
                            }}
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={freightCountedByDriverShared || Boolean(currentEditable.freight_counted_by_driver)}
                      disabled={isViewingSaved}
                      onChange={(e) => {
                        const nextValue = e.target.checked;
                        setFreightCountedByDriverShared(nextValue);
                        setEditableByIndex((prev) => {
                          const updated = { ...prev };
                          Object.keys(updated).forEach((key) => {
                            updated[key] = {
                              ...(updated[key] || {}),
                              freight_counted_by_driver: nextValue,
                            };
                          });
                          if (!updated[selectedIndex]) {
                            updated[selectedIndex] = { freight_counted_by_driver: nextValue };
                          }
                          return updated;
                        });
                      }}
                      className="h-4 w-4 rounded border border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    By Driver
                  </label>
                </section>

                <section className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Carrier Information</div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-xs text-slate-700">
                      <thead className="bg-slate-100 text-[11px] uppercase text-slate-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Handling Units</th>
                          <th className="px-3 py-2 text-right">Package (cases)</th>
                          <th className="px-3 py-2 text-right">Weight</th>
                          <th className="px-3 py-2 text-right">Cube</th>
                          <th className="px-3 py-2 text-left">Commodity Description</th>
                          <th className="px-3 py-2 text-left">NMFC</th>
                          <th className="px-3 py-2 text-left">Class</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCarrierLines.map((line, lineIndex) => (
                          <tr key={`carrier-line-${lineIndex}`} className="border-t border-slate-100">
                            <td className="px-3 py-2">{formatValue(line.handling_units_text)}</td>
                            <td className="px-3 py-2 text-right">{formatValue(line.packages)}</td>
                            <td className="px-3 py-2 text-right">{formatValue(line.weight_lbs)}</td>
                            <td className="px-3 py-2 text-right">{formatValue(line.cube)}</td>
                            <td className="px-3 py-2">
                              {isViewingSaved ? (
                                formatValue(isBlankCarrierLine(line) ? "" : line.commodity_description)
                              ) : (
                                <input
                                  type="text"
                                  value={isBlankCarrierLine(line) ? "" : commodityOverride}
                                  onChange={(e) =>
                                    setEditableByIndex((prev) => ({
                                      ...prev,
                                      [selectedIndex]: {
                                        ...(prev[selectedIndex] || {}),
                                        commodity_description_override: e.target.value,
                                      },
                                    }))
                                  }
                                  className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                />
                              )}
                            </td>
                            <td className="px-3 py-2">{formatValue(line.nmfc)}</td>
                            <td className="px-3 py-2">{formatValue(line.freight_class)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                          <td className="px-3 py-2">Totals</td>
                          <td className="px-3 py-2 text-right">
                            {carrierTotals.allEmpty ? "" : carrierTotals.totals.packages}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {carrierTotals.allEmpty ? "" : carrierTotals.totals.weight_lbs}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {carrierTotals.allEmpty ? "" : carrierTotals.totals.cube}
                          </td>
                          <td className="px-3 py-2" />
                          <td className="px-3 py-2" />
                          <td className="px-3 py-2" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
      {manualShipToOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelOverrides} />
          <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800">Ship To Address Not Found</h3>
            <p className="mt-1 text-sm text-slate-500">
              We couldn't find a Walmart DC address for the node(s) below. Please enter the Ship To address manually or cancel.
            </p>
            <div className="mt-4 space-y-4">
              {missingShipToNodesState.map((node) => {
                const entry = manualShipToOverridesState[node] || {};
                return (
                  <div key={`node-${node}`} className="rounded-lg border border-slate-200 p-4">
                    <div className="text-sm font-semibold text-slate-700">Node {node}</div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="text-xs font-semibold text-slate-600">Address Line 1*</label>
                        <input
                          type="text"
                          value={entry.address_line_1 || ""}
                          onChange={(e) => handleOverrideChange(node, "address_line_1", e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-semibold text-slate-600">Address Line 2</label>
                        <input
                          type="text"
                          value={entry.address_line_2 || ""}
                          onChange={(e) => handleOverrideChange(node, "address_line_2", e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600">City*</label>
                        <input
                          type="text"
                          value={entry.city || ""}
                          onChange={(e) => handleOverrideChange(node, "city", e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600">State*</label>
                        <input
                          type="text"
                          value={entry.state || ""}
                          onChange={(e) => handleOverrideChange(node, "state", e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600">Zip Code*</label>
                        <input
                          type="text"
                          value={entry.zip_code || ""}
                          onChange={(e) => handleOverrideChange(node, "zip_code", e.target.value)}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {manualShipToError && <div className="mt-3 text-sm text-red-600">{manualShipToError}</div>}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelOverrides}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmOverrides}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
              >
                Use This Address
              </button>
            </div>
          </div>
        </div>
      )}
      {resetRoutingConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelResetRoutingRows} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800">Reset routing rows?</h3>
            <p className="mt-1 text-sm text-slate-500">
              This will discard all routing row edits and PO splits for this document.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelResetRoutingRows}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmResetRoutingRows}
                className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-rose-700"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
